import React from 'react';
import LegalPage, { List, P, Placeholder, Section } from './LegalPage';

/**
 * Terms and Conditions TEMPLATE.
 *
 * Sample only — see the in-page banner. Terms are not strictly mandated by law
 * the way GDPR privacy disclosures are, but they protect the operator (rules of
 * use, liability limits, termination, governing law). Replace every
 * <Placeholder> and have a qualified adviser review before production use.
 */
const TermsPage: React.FC = () => {
  return (
    <LegalPage title="Terms and Conditions" lastUpdated={<Placeholder>date</Placeholder>}>
      <Section heading="1. Agreement to these terms">
        <P>
          These Terms and Conditions (&quot;Terms&quot;) govern your access to and use of the
          Sulis-Liiga badminton league management application (the &quot;Service&quot;), operated by{' '}
          <Placeholder>legal entity / organiser name</Placeholder> (&quot;we&quot;, &quot;us&quot;).
          By creating an account or using the Service, you agree to these Terms and to our{' '}
          <a href="/privacy" className="text-primary-600 hover:underline">
            Privacy Policy
          </a>
          . If you do not agree, do not use the Service.
        </P>
      </Section>

      <Section heading="2. Eligibility">
        <P>
          You must be able to form a binding agreement to use the Service.{' '}
          <Placeholder>
            State the minimum age to register and any rules for minors participating in the league
          </Placeholder>
          . You are responsible for ensuring your use of the Service complies with any rules of your
          club, venue, or league organiser.
        </P>
      </Section>

      <Section heading="3. Your account">
        <List>
          <li>You must provide accurate registration information and keep it up to date.</li>
          <li>
            You are responsible for keeping your password confidential and for all activity under
            your account.
          </li>
          <li>Notify us promptly of any unauthorised use of your account.</li>
          <li>You may not share, sell, or transfer your account to anyone else.</li>
        </List>
      </Section>

      <Section heading="4. Acceptable use">
        <P>When using the Service, you agree not to:</P>
        <List>
          <li>break any applicable law or infringe anyone&apos;s rights;</li>
          <li>
            submit false game results, manipulate points or standings, or otherwise abuse the league
            mechanics;
          </li>
          <li>harass, threaten, or abuse other players in messages or communications;</li>
          <li>
            attempt to gain unauthorised access to the Service, other accounts, or its underlying
            systems;
          </li>
          <li>upload malicious code or interfere with the Service&apos;s operation.</li>
        </List>
      </Section>

      <Section heading="5. League play, results and disputes">
        <P>
          The Service lets players form teams, propose and schedule games, submit and approve
          results, and raise disputes. You agree to record results honestly and in good faith.
        </P>
        <P>
          Results, points, promotion/relegation, and dispute outcomes are governed by the applicable
          league rules and, where provided, resolved by a league administrator.{' '}
          <Placeholder>
            Reference the official league rules document and describe who has final say over
            disputes
          </Placeholder>
          . We are not responsible for outcomes that result from information you or other players
          submit.
        </P>
      </Section>

      <Section heading="6. User content">
        <P>
          You retain ownership of the content you submit (such as messages, team names, and result
          data). You grant us a licence to host, display, and process that content as necessary to
          operate the Service. You are responsible for the content you submit and must have the
          right to share it.
        </P>
      </Section>

      <Section heading="7. Intellectual property">
        <P>
          The Service, including its software, design, and branding, is owned by us or our licensors
          and is protected by intellectual property laws. You may not copy, modify, distribute, or
          create derivative works from the Service except as permitted by law or with our written
          permission.
        </P>
      </Section>

      <Section heading="8. Availability and changes to the Service">
        <P>
          We provide the Service on an &quot;as is&quot; and &quot;as available&quot; basis. We may
          modify, suspend, or discontinue any part of the Service at any time, and we do not
          guarantee that it will always be available or error-free.
        </P>
      </Section>

      <Section heading="9. Disclaimers and limitation of liability">
        <P>
          To the fullest extent permitted by law, we exclude all warranties not expressly stated in
          these Terms. We are not liable for indirect or consequential loss, or for loss arising
          from your misuse of the Service or from data you or other users submit.{' '}
          <Placeholder>
            Have an adviser tailor the liability cap and exclusions to Finnish/EU consumer law —
            some liability (e.g. for death, personal injury, or gross negligence) cannot be excluded
          </Placeholder>
          .
        </P>
      </Section>

      <Section heading="10. Suspension and termination">
        <P>
          You may stop using the Service and close your account at any time. We may suspend or
          terminate your access if you breach these Terms or use the Service in a way that harms
          other users or the Service. Provisions that by their nature should survive termination
          (such as intellectual property and liability terms) will continue to apply.
        </P>
      </Section>

      <Section heading="11. Changes to these terms">
        <P>
          We may update these Terms from time to time. We will update the &quot;Last updated&quot;
          date above and, where changes are material, notify you through the Service or by email.
          Continued use after changes take effect means you accept the updated Terms.
        </P>
      </Section>

      <Section heading="12. Governing law and disputes">
        <P>
          These Terms are governed by the laws of Finland, and disputes are subject to the
          jurisdiction of the Finnish courts, unless mandatory consumer-protection law grants you
          other rights.{' '}
          <Placeholder>
            Confirm governing law, jurisdiction, and any consumer dispute body
          </Placeholder>
          .
        </P>
      </Section>

      <Section heading="13. Contact us">
        <P>
          Questions about these Terms can be sent to{' '}
          <Placeholder>support / contact email</Placeholder>.
        </P>
      </Section>
    </LegalPage>
  );
};

export default TermsPage;
